import React from 'react';

import { useAsyncFn } from 'react-use';
import { enqueueSnackbar } from 'notistack';

import { LbModal } from '../../../../shared/components/modals';
import { CUSTOMER_TYPE } from '../../../../shared/constants/customer/customerType';
import { downloadFile } from '../../../../shared/utils/apiUtils';
import { getScanService } from '../../../services/scanService/getScanService';
import { ScanMetadata } from '../../../interfaces/scanMetadata';
import { useScansMetadata } from '../../../hooks/useScansMetadata/useScansMetadata';
import progressReportPreviewImg from '../../../../shared/assets/image/progress-report-preview.webp';
import classes from './ProgressReportModal.module.scss';
import { ReportForm } from './ReportForm/ReportForm';
import { ProgressReportParams } from '../../../services/scanService/types';

type ProgressReportModalProps = {
  customerId: number;
  entityId?: number;
  scansMetadata?: ScanMetadata[];
  onClose: () => void;
};

const customerType = CUSTOMER_TYPE.SCAN_IQ;
const scanService = getScanService(customerType);

const FORM_ID = 'progress-report-form';

const ProgressReportModal = ({
  customerId,
  entityId,
  scansMetadata: scansMetadataProp,
  onClose,
}: ProgressReportModalProps) => {
  const { scansMetadata, isLoading: isScansLoading } = useScansMetadata({
    customerType,
    customerId,
    entityId,
    scansMetadata: scansMetadataProp,
  });

  const handleGenerateReport = async (params: ProgressReportParams | null) => {
    if (!params) {
      return;
    }

    const { pdfUrl } = await scanService.getProgressReportUrl(customerId, params);

    downloadFile(pdfUrl);
    enqueueSnackbar('Your file has been generated and downloaded successfully.', {
      variant: 'success',
      autoHideDuration: 3000,
    });
    onClose();
  };

  const [{ loading: isGenerating }, handleFormSubmit] = useAsyncFn(handleGenerateReport);

  const isLoading = isScansLoading || isGenerating;
  const isDataReady = !isScansLoading;

  return (
    <LbModal
      open
      title="Progress Report"
      text="An easy-to-read summary of your client's progress, trends, and areas to focus on."
      isLoading={isLoading}
      onClose={onClose}
      primaryBtnConfig={{
        text: 'Generate and Download',
        formId: FORM_ID,
        disabled: isLoading || !scansMetadata.length,
      }}
      secondaryBtnConfig={{ onClick: onClose }}
    >
      <div className={classes.content}>
        <img
          src={progressReportPreviewImg}
          alt="Progress report preview"
          className={classes.preview}
        />
        {isDataReady && (
          <ReportForm
            formId={FORM_ID}
            onSubmit={handleFormSubmit}
            scansMetadata={scansMetadata}
          />
        )}
      </div>
    </LbModal>
  );
};

export { ProgressReportModal };
